/**
 * The one place this app talks to `apps/api`.
 *
 * Every request is same-origin and carries the session cookie (§2.3). There is
 * no token in memory, no `Authorization` header, and no refresh dance — the
 * cookie is `httpOnly`, so this file could not read it if it wanted to, which
 * is the point.
 */

/**
 * `apps/api/src/routes/errors.ts` — every failure the SPA can see arrives as
 * `{ error: { code, message, details? }, requestId }`.
 */
export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
  requestId: string | null;
}

/**
 * A denial arrives with the resolution that produced it, from the same resolver
 * §4.8's views render. Screens that catch a 403 can therefore explain it rather
 * than say "forbidden".
 */
export interface ForbiddenDetails {
  capability: string;
  subjectType: 'node' | 'space' | 'role';
  subjectId: string | null;
  decision: Decision;
}

export interface Decision {
  allowed: boolean;
  grantId: string | null;
  principalType: 'person' | 'role' | null;
  grantedOnType: 'node' | 'space' | null;
  grantedOnId: string | null;
  distance: number | null;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId: string | null;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error.code;
    this.details = body.error.details;
    this.requestId = body.requestId;
  }

  /** Narrowed accessor for the 403 shape above; undefined for every other status. */
  get forbidden(): ForbiddenDetails | undefined {
    if (this.status !== 403) return undefined;
    const details = this.details as ForbiddenDetails | undefined;
    return details && typeof details === 'object' && 'decision' in details ? details : undefined;
  }
}

/**
 * Relative by default, which is what both deployed environments want — the App
 * Platform ingress puts `/api` and `/auth` on this same origin, and the Vite
 * dev proxy reproduces that locally. `VITE_API_URL` exists as the escape hatch
 * for a split-origin deployment; it is set to the app's own URL in
 * `.do/app.*.yaml`, which is the same thing as leaving it empty.
 */
const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

export type QueryValue = string | number | boolean | null | undefined;

function withQuery(path: string, query?: Record<string, QueryValue>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `${path}?${encoded}` : path;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const init: RequestInit = {
    method: options.method ?? 'GET',
    // Without this the cookie is not sent and every route answers 401.
    credentials: 'include',
    headers: options.body === undefined ? {} : { 'content-type': 'application/json' },
    ...(options.signal ? { signal: options.signal } : {}),
  };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);

  return fetch(`${BASE}${withQuery(path, options.query)}`, init);
}

/**
 * Turns a failed response into an `ApiError`, reading the body as JSON even
 * when the request asked for text: `apps/api/src/routes/errors.ts` answers every
 * failure with the same JSON envelope regardless of what the handler would have
 * returned on success.
 */
function failure(response: Response, parsed: unknown): ApiError {
  const body = parsed as ApiErrorBody | null;
  return new ApiError(
    response.status,
    body?.error
      ? body
      : {
          error: { code: 'unknown', message: response.statusText || 'Request failed.' },
          requestId: response.headers.get('x-request-id'),
        },
  );
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed: unknown = text.length > 0 ? JSON.parse(text) : null;

  if (!response.ok) throw failure(response, parsed);

  return parsed as T;
}

/**
 * The same request, for the one endpoint that does not answer in JSON.
 *
 * `GET /api/documents/:id/markdown` serves `text/markdown` — §5.1's wire format,
 * which is what copy-as-markdown and the raw endpoint in §12 hand a person. Put
 * through {@link request} it would reach `JSON.parse` and throw a `SyntaxError`
 * that says nothing about markdown, so the response is read as text here and the
 * error path still parses JSON, because a failure is still the error envelope.
 *
 * `x-document-source` rides along: the API says which of the three bodies (§7.4)
 * it served, and a "copied" toast that cannot distinguish the draft from the
 * published version is a toast that lies to an editor half the time.
 */
export async function requestText(
  path: string,
  options: RequestOptions = {},
): Promise<{ text: string; source: string | null }> {
  const response = await send(path, options);
  const text = await response.text();

  if (!response.ok) {
    let parsed: unknown = null;
    try {
      parsed = text.length > 0 ? JSON.parse(text) : null;
    } catch {
      // A non-JSON error body is a proxy or an ingress answering, not the API.
    }
    throw failure(response, parsed);
  }

  return { text, source: response.headers.get('x-document-source') };
}

export const api = {
  get: <T>(path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) =>
    request<T>(path, { ...(query ? { query } : {}), ...(signal ? { signal } : {}) }),
  text: (path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) =>
    requestText(path, { ...(query ? { query } : {}), ...(signal ? { signal } : {}) }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body ?? {} }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
